import React from 'react';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import Accordion from '@material-ui/core/Accordion';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import Typography from '@material-ui/core/Typography';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import IconButton from '@material-ui/core/IconButton';
import { Grid } from '@material-ui/core';

import PGELogo from '../../../logos/PGELogo';
import RichText from '../../../rich-text';
import colors from '../../../../themes/main-colors';
import { useTranslation } from '../../../../hooks/useTranslation';

interface Props {
  accordionItems: any;
  accordionHeader?: string;
  isMobile?: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    logo: {
      height: 54,
      width: 54,
      [theme.breakpoints.down('sm')]: {
        height: 32.24,
        width: 32.24,
      },
      '& .angles': {
        fill: colors.noirBlur,
      },
      '& .letters': {
        fill: colors.noirBlur,
      },
    },
    headerSection: {
      paddingBottom: theme.spacing(3.625),
      display: 'flex',
      alignItems: 'center',
      [theme.breakpoints.down('sm')]: {
        paddingTop: 0,
        paddingBottom: 4,
      },
    },
    headerText: {
      paddingLeft: 30,
      [theme.breakpoints.down('sm')]: {
        paddingLeft: 22,
        fontSize: '1.25rem',
        lineHeight: '1.625rem',
        fontWeight: 'bold',
      },
    },
    iconButton: {
      padding: 0,
      '&.MuiIconButton-root:hover': {
        background: 'transparent',
      },
    },
    bodySection: {
      [theme.breakpoints.down('sm')]: {
        overflow: 'auto',
        boxShadow: 'none',
        filter: 'none',
        padding: '24px 12px',
      },
    },
    tabTitle: {
      lineHeight: '1.375rem',
      fontWeight: 'normal',
      color: colors.noirBlur,
      [theme.breakpoints.down('sm')]: {
        fontSize: '1rem',
        lineHeight: '1.125rem',
        fontWeight: 'bold',
      },
    },
    accordionDetailSection: {
      padding: '0px 24px 24px 24px',
      [theme.breakpoints.down('sm')]: {
        padding: '0px 0px 10px 0px',
      },
    },
    accordionTitleSection: {
      minHeight: 72,
      padding: '0 18px 0 24px',
      [theme.breakpoints.down('sm')]: {
        minHeight: 48,
        padding: 0,
      },
    },
    accordionWrapper: {
      marginBottom: 10,
      borderRadius: 5,
      boxShadow: `0px 3px 6px ${colors.boxShadowColor}`,
      filter: `drop-shadow(0px 3px 6px ${colors.boxShadowColor})`,
      '&.MuiAccordion-root:before': {
        display: 'none',
      },
      [theme.breakpoints.down('sm')]: {
        lineHeight: '1.125rem',
        filter: 'none',
        boxShadow: 'none',
        borderRadius: 0,
        marginBottom: 0,
        borderBottom: `1px solid ${colors.fordGray}`,
        '&.MuiAccordion-root.Mui-expanded': {
          margin: 0,
        },
        '&:first-child': {
          borderTop: `1px solid ${colors.fordGray}`,
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
        },
        '&:last-child': {
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
        },
      },
    },
    tabBody: {
      margin: 0,
      '& > p': {
        fontSize: '1rem',
        lineHeight: '1.25rem',
        color: colors.noirBlur,
        [theme.breakpoints.down('sm')]: {
          paddingBottom: 10,
        },
      },
    },
    expandMoreIconStyle: {
      color: colors.noirBlur,
    },
  }),
);

const outageMapFAQAccordion = (props: Props): React.ReactElement => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { accordionItems, accordionHeader, isMobile } = props;

  const initialItems: any = [...accordionItems];

  const [expands, setExpands] = React.useState<Array<boolean>>([
    ...initialItems?.fill(false),
  ]);

  // Handle Expand and Collapse of Accordion
  const handleChange = (index: number) => (
    event: React.ChangeEvent<{}>,
    isExpanded: boolean,
  ): void => {
    const data: Array<boolean> = [...expands];
    data[index] = isExpanded;
    setExpands(data);
  };

  const accordion = accordionItems?.map(
    (panel: any, index: number): React.ReactElement => {
      return (
        <Accordion
          data-testid={index}
          expanded={expands[index]}
          onChange={handleChange(index)}
          key={index}
          className={classes.accordionWrapper}
        >
          <AccordionSummary
            data-testid={`panel${index}bh-header`}
            expandIcon={
              <ExpandMoreIcon className={classes.expandMoreIconStyle} />
            }
            aria-controls={`panel${index}bh-content`}
            id={`panel${index}bh-header`}
            className={classes.accordionTitleSection}
          >
            <Grid>
              {(panel?.tabTeaser || panel.tabTitle) && (
                <Grid item>
                  <Typography className={classes.tabTitle}>
                    {panel.tabTitle}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </AccordionSummary>
          <AccordionDetails className={classes.accordionDetailSection}>
            {panel?.tabBody?.raw ? (
              <RichText className={classes.tabBody}>{panel?.tabBody}</RichText>
            ) : (
              <Typography className={classes.tabBody}>
                {panel?.tabBody}
              </Typography>
            )}
          </AccordionDetails>
        </Accordion>
      );
    },
  );

  return (
    <>
      <div className={classes.headerSection}>
        <IconButton edge="end" aria-label="home" className={classes.iconButton}>
          <PGELogo
            viewBox="0 0 108 108"
            color="#1C304A"
            className={classes.logo}
          />
        </IconButton>
        <Typography variant={'h1'} className={classes.headerText}>
          {accordionHeader ? accordionHeader : t('USING_THE_OUTAGE_MAP_FAQS')}
        </Typography>
      </div>
      <div className={classes.bodySection}>{accordion}</div>
    </>
  );
};

export default outageMapFAQAccordion;
